/**
 * Half-day blocks.
 *
 * Time below the UI is an integer, never a date. Weekends are not in the
 * sequence at all — block N+1 after a Friday PM is the following Monday AM —
 * so "three blocks of work" always means three half-days of *capacity*,
 * not three half-days of calendar.
 *
 *   block 0 = 2026-01-05 (a Monday) AM
 *   block 1 = 2026-01-05 PM
 *   block 2 = 2026-01-06 AM
 *   ...
 *   block = workingDaysSinceEpoch * 2 + (AM ? 0 : 1)
 */

export const EPOCH_ISO = "2026-01-05";
export const BLOCKS_PER_DAY = 2;

const MS_PER_DAY = 86_400_000;
const EPOCH_UTC = Date.UTC(2026, 0, 5);

/** Parse a YYYY-MM-DD date as UTC midnight. Avoids local-timezone drift. */
export function parseISODate(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) throw new Error(`not a YYYY-MM-DD date: ${iso}`);
  return Date.UTC(y, m - 1, d);
}

export function toISODate(utcMs: number): string {
  return new Date(utcMs).toISOString().slice(0, 10);
}

/** 0 = Sunday … 6 = Saturday */
function dayOfWeek(utcMs: number): number {
  return new Date(utcMs).getUTCDay();
}

export function isWorkingDay(utcMs: number): boolean {
  const d = dayOfWeek(utcMs);
  return d >= 1 && d <= 5;
}

/**
 * Working days from the epoch to `utcMs`, exclusive of the target.
 * Negative if before the epoch. Counts Mon–Fri only.
 */
export function workingDaysFromEpoch(utcMs: number): number {
  const rawDays = Math.round((utcMs - EPOCH_UTC) / MS_PER_DAY);
  if (rawDays === 0) return 0;

  const sign = rawDays > 0 ? 1 : -1;
  const weeks = Math.trunc(rawDays / 7);
  let days = weeks * 5;
  let cursor = EPOCH_UTC + weeks * 7 * MS_PER_DAY;

  while (cursor !== utcMs) {
    cursor += sign * MS_PER_DAY;
    // Walking forward we count the day we arrive at; walking backward we count
    // the day we left, so both directions count the same set of working days.
    const counted = sign > 0 ? cursor : cursor + MS_PER_DAY;
    if (isWorkingDay(counted)) days += sign;
  }
  return days;
}

/** Nth working day after the epoch, as UTC ms. */
export function workingDayToUtc(n: number): number {
  // The walk below only ends when `remaining` reaches exactly zero, so a NaN or
  // fractional day would never end it. Refuse rather than hang a request.
  if (!Number.isInteger(n)) throw new RangeError(`workingDayToUtc needs a whole day number, got ${n}`);
  const weeks = Math.trunc(n / 5);
  let remaining = n - weeks * 5;
  let cursor = EPOCH_UTC + weeks * 7 * MS_PER_DAY;
  const step = remaining >= 0 ? MS_PER_DAY : -MS_PER_DAY;
  while (remaining !== 0) {
    cursor += step;
    if (isWorkingDay(cursor)) remaining -= remaining > 0 ? 1 : -1;
  }
  // Landing on a weekend (only possible at n=0 offsets) rolls forward.
  while (!isWorkingDay(cursor)) cursor += MS_PER_DAY;
  return cursor;
}

export interface BlockDate {
  date: string;
  half: "AM" | "PM";
}

export function blockToDate(block: number): BlockDate {
  const day = Math.floor(block / BLOCKS_PER_DAY);
  const half = block - day * BLOCKS_PER_DAY === 0 ? "AM" : "PM";
  return { date: toISODate(workingDayToUtc(day)), half };
}

export function dateToBlock(iso: string, half: "AM" | "PM" = "AM"): number {
  const utc = parseISODate(iso);
  if (!isWorkingDay(utc)) {
    // A weekend date has no block; the next working day's AM is the honest answer.
    let cursor = utc;
    while (!isWorkingDay(cursor)) cursor += MS_PER_DAY;
    return workingDaysFromEpoch(cursor) * BLOCKS_PER_DAY;
  }
  return workingDaysFromEpoch(utc) * BLOCKS_PER_DAY + (half === "AM" ? 0 : 1);
}

/** Whole blocks needed for `hours`, at `hoursPerBlock`. Rounds up — SCH-3. */
export function hoursToBlocks(hours: number, hoursPerBlock: number): number {
  if (hours <= 0) return 0;
  if (hoursPerBlock <= 0) throw new Error("hoursPerBlock must be positive");
  return Math.ceil(hours / hoursPerBlock);
}

/**
 * Effective hours in one half-day block for a resource.
 * fte × 8h × capacity% ÷ 2 blocks.
 */
export function blockCapacityHours(fte: number, capacityPct: number, hoursPerDay = 8): number {
  return (fte * hoursPerDay * (capacityPct / 100)) / BLOCKS_PER_DAY;
}
